// Billing is separate from role/provider controls. These decisions only remove
// spend authority; they never grant a campaign permission or call Meta.
export function campaignSpendAccess(billing, companyId, now = Date.now()) {
  if (billing?.status === 'disabled' && billing.enabled === false && (!billing.companyId || billing.companyId === companyId)) return { allowed: true, reason: 'allowed' };
  if (billing?.companyId !== companyId || billing.status !== 'ready' || !billing.billing || (billing.billing.companyId && billing.billing.companyId !== companyId)) {
    return { allowed: false, reason: 'unconfirmed', title: 'Subscription status is unavailable', description: 'Paid access could not be verified. Check your company subscription before trying this action. No campaign changes have been requested.' };
  }
  const value = billing.billing;
  const expired = value.enabled !== false && value.accessExpiresAt && Date.parse(value.accessExpiresAt) <= now;
  if (billing.canResume === true && !expired && !['disputed', 'refunded'].includes(value.status)) return { allowed: true, reason: 'allowed' };
  const description = value.status === 'disputed'
    ? 'A subscription payment is under dispute. Resuming ads and increasing budgets are blocked during Finance’s review.'
    : value.status === 'refunded' ? 'The current paid period was fully refunded. Confirmed paid access is required before resuming ads or increasing budgets.'
      : value.status === 'past_due' && billing.canCreate
        ? 'You can create campaigns during the 14-day renewal grace period. Confirmed paid access is required to resume ads or increase budgets.'
        : ['pending', 'incomplete'].includes(value.status)
          ? 'Payment has not been confirmed. Confirmed paid access is required to resume ads or increase budgets.'
          : 'Your company needs confirmed paid subscription access to resume ads or increase budgets. You can keep browsing the app.';
  return { allowed: false, reason: 'subscription_required', title: 'Paid access is required', description };
}

function monthlyMinor(value, exponent) {
  if (typeof value !== 'string' || value.length > 32 || !/^\d+(?:\.\d{1,2})?$/.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  if (/[1-9]/.test(fraction.slice(exponent))) return null;
  const minor = BigInt(whole) * (10n ** BigInt(exponent)) + BigInt(fraction.slice(0, exponent).padEnd(exponent, '0') || '0');
  return minor > 0n && minor <= BigInt(Number.MAX_SAFE_INTEGER) ? minor : null;
}

export function campaignBudgetBaseline(detail, now = Date.now()) {
  const baseline = detail?.budgetBaseline;
  const at = Date.parse(baseline?.verifiedAt || '');
  return baseline?.trustworthy === true && Number.isSafeInteger(baseline.dailyMinor) && baseline.dailyMinor > 0
    && /^[A-Z]{3}$/.test(baseline.currency || '') && Number.isInteger(baseline.minorUnitExponent) && baseline.minorUnitExponent >= 0 && baseline.minorUnitExponent <= 3
    && Number.isFinite(at) && at <= now && now - at <= 300_000 ? baseline : null;
}

export function campaignBudgetAccess({ billing, companyId, detail, monthlyMajor, now = Date.now() }) {
  const spend = campaignSpendAccess(billing, companyId, now);
  const baseline = detail?.campaign?.companyId === companyId ? campaignBudgetBaseline(detail, now) : null;
  const minor = monthlyMinor(monthlyMajor, baseline?.minorUnitExponent ?? 2);
  const daily = minor === null ? null : (minor * 5n + 76n) / 152n;
  if (baseline && daily !== null && daily > 0n && daily === BigInt(baseline.dailyMinor)) return { allowed: true, reason: 'unchanged', unchanged: true };
  if (baseline && daily !== null && daily > 0n && daily < BigInt(baseline.dailyMinor)) return { allowed: true, reason: 'decrease' };
  if (spend.allowed) return { allowed: true, reason: 'allowed' };
  if (spend.reason === 'unconfirmed') return spend;
  if (!baseline || daily === null || daily <= 0n) return {
    allowed: false, reason: 'budget_unconfirmed', title: 'Current budget needs verification',
    description: 'Refresh campaign controls to verify the current Meta budget before changing it. Without confirmed paid access, only a verified decrease is available.',
  };
  return { ...spend, title: 'Paid access is required to increase budgets' };
}
